import { datepickerToISOFormat } from '@yes.technology/react-toolkit'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import {
  Activity,
  ActivityGroup,
  ActivityModel,
  ActivityModelContentType,
  ActivityValue,
  FlatInteractionModelData,
  Step,
  InteractionModel,
  NestableGroupValue
} from 'types/shared'
import api from './api'
import { AllowedLocales } from './date'
import pick from 'lodash.pick'
import { flatValuesToOldActivities } from 'modules/interaction/utils/flatValuesToOldActivities'
import { ActivityTransformersMap } from 'interactionModel/transform/ActivityTransformer.types'
import getTransformedActivities from 'interactionModel/transform/getTransformedActivities'
import useDrawActivityTransformer from 'interactionModel/transform/useDrawActivityTransformer'
import { CURRENT_GROUP_UUID } from 'modules/interaction/utils/constants'

type GroupValidationCallbackRequestData = {
  uuid_interaction_model_item_group: string
  activity_models: { uuid_activity_model: string; value: ActivityValue }[]
}

type ActivityValuesToGroupValidationFormatProps = {
  stepUuid: string
  activityValues: FlatInteractionModelData['activity_values']
}

const nestedGroupsToValueList = (
  nestedGroups: NestableGroupValue
): { uuid_activity_model: string; value: ActivityValue }[] => {
  return nestedGroups.flatMap((group) =>
    Object.entries(group.values).flatMap(([uuid_activity_model, value]) => {
      if (Array.isArray(value)) {
        return nestedGroupsToValueList(value)
      }

      return { uuid_activity_model, value }
    })
  )
}

const activityValuesToValueList = (
  activityValues: FlatInteractionModelData['activity_values']
): { uuid_activity_model: string; value: ActivityValue }[] => {
  return Object.entries(activityValues).flatMap(
    ([uuid_activity_model, value]) => {
      if (Array.isArray(value)) {
        return nestedGroupsToValueList(value)
      }

      return { uuid_activity_model, value }
    }
  )
}

const activityValuesToGroupValidationFormat = ({
  activityValues,
  stepUuid
}: ActivityValuesToGroupValidationFormatProps): GroupValidationCallbackRequestData => {
  return {
    uuid_interaction_model_item_group: stepUuid,
    activity_models: activityValuesToValueList(activityValues)
  }
}

function formatInteractionModelCallback(
  interactionModel: FlatInteractionModelData
) {
  const steps = interactionModel.steps.map(({ uuid: stepUuid }) => {
    return activityValuesToGroupValidationFormat({
      activityValues: interactionModel.activity_values,
      stepUuid
    })
  })

  const requestBody = {
    uuid_interaction_model: interactionModel.uuid,
    interaction_model_item_groups: steps
  }

  return requestBody
}

const objectSelections: ActivityModelContentType[] = [
  'explicit_uniselection_objectclass',
  'explicit_multiselection_objectclass',
  'standard_multiselection',
  'standard_uniselection',
  'explicit_uniselection_accordion',
  'explicit_multiselection_accordion',
  'autocomplete_uniselection',
  'autocomplete_multiselection'
]

function isObjectSelection(contentType: ActivityModelContentType) {
  return objectSelections.includes(contentType)
}

function isContentTypeObject(
  activityModel: Pick<
    ActivityModel,
    'content_type' | 'information_request_filteraction'
  >
) {
  return (
    activityModel?.information_request_filteraction ||
    isObjectSelection(activityModel.content_type)
  )
}

const parseActivityIdValuePair = (
  activity: Activity,
  activityModels: FlatInteractionModelData['activity_models']
): [string, ActivityValue] => {
  const uuid = activity.uuid_activity_model

  const activityModel = activityModels[uuid]

  const value =
    activity.des && isContentTypeObject(activityModel)
      ? { id: activity.des, des: activity.complement || activity.des }
      : activity.des

  return [uuid, value]
}

const parseActivityGroupRecursively = (
  activitiesInGroup: Activity[],
  activityModels: FlatInteractionModelData['activity_models']
): NestableGroupValue[0] => {
  const groupUuidActivityIndex = activitiesInGroup.findIndex(
    (activityInGroup) =>
      activityInGroup.uuid_activity_model === CURRENT_GROUP_UUID
  )
  const groupUuidActivity =
    groupUuidActivityIndex > -1
      ? activitiesInGroup.splice(groupUuidActivityIndex, 1)[0]
      : { des: '' }

  const uuid = groupUuidActivity.des || uuidv4()

  const values = Object.fromEntries(
    activitiesInGroup.map((activityInGroup) => {
      const activityModel = activityModels[activityInGroup.uuid_activity_model]

      if (activityModel?.content_type === 'activity_group') {
        // Handle nested groups recursively
        const nestedGroups: Activity[][] = activityInGroup.des
          ? JSON.parse(activityInGroup.des)
          : []
        const nestedGroupValues = nestedGroups.map((nestedGroup) =>
          parseActivityGroupRecursively(nestedGroup, activityModels)
        )
        return [activityInGroup.uuid_activity_model, nestedGroupValues]
      }

      return parseActivityIdValuePair(activityInGroup, activityModels)
    })
  )

  return {
    uuid,
    values
  }
}

function setActivityGroupActivities(
  flatData: FlatInteractionModelData,
  activity: Activity
) {
  const groups: Activity[][] = activity.des ? JSON.parse(activity.des) : []

  flatData.activity_values[activity.uuid_activity_model] = groups.map(
    (activitiesInGroup) =>
      parseActivityGroupRecursively(activitiesInGroup, flatData.activity_models)
  )
}

export function setFlatInteractionModelActivities(
  flatData: FlatInteractionModelData,
  activities: Activity[]
) {
  activities.forEach((activity) => {
    const activityModel = flatData.activity_models[activity.uuid_activity_model]

    if (!activityModel) {
      return
    }

    if (activityModel.content_type === 'activity_group') {
      setActivityGroupActivities(flatData, activity)
      return
    }

    flatData.activity_values[activity.uuid_activity_model] =
      parseActivityIdValuePair(activity, flatData.activity_models)[1]
  })
}

function getActivityFromActivityGroup(activityModel: ActivityGroup) {
  const groups = activityModel.activity_models

  const groupsActivities = groups.map((group) => {
    const activities = group.map(getActivityFromActivityModel)
    const nonEmptyActivities = activities.filter((activity) => activity.des)
    return nonEmptyActivities
  })

  const nonEmptyGroups = groupsActivities.filter((group) => group.length > 0)

  const activityValue =
    nonEmptyGroups.length > 0 ? JSON.stringify(nonEmptyGroups) : ''

  return {
    uuid_activity_model: activityModel.uuid,
    des: activityValue
  }
}

export function getActivityFromActivityModel(
  activityModel: ActivityModel
): Activity {
  const isActivityGroup = activityModel.content_type === 'activity_group'

  if (isActivityGroup) {
    return getActivityFromActivityGroup(activityModel)
  }

  const contentIsObject = isContentTypeObject(activityModel)

  const activityModelValue = activityModel.value
  const isValueObject = typeof activityModelValue === 'object'

  const uuid = isValueObject ? activityModelValue.id : ''

  const description = isValueObject
    ? activityModelValue.des
    : activityModelValue || ''

  return {
    uuid_activity_model: activityModel.uuid,
    des: contentIsObject ? uuid : description,
    ...(contentIsObject && { complement: description })
  }
}

// Retorna os uuids das activityModels do tipo especificado
export function getUuidsForContentType(
  activityModels: FlatInteractionModelData['activity_models'],
  contentType: ActivityModelContentType
) {
  return Object.entries(activityModels).flatMap(([, activityModel]) =>
    activityModel.content_type === contentType ? activityModel.uuid : []
  )
}

export function handleAgreementTerm(
  activities: Activity[],
  activityModels: FlatInteractionModelData['activity_models']
) {
  const uuids = getUuidsForContentType(activityModels, 'agreement_term')

  if (uuids.length > 0) {
    uuids.forEach((uuid) => {
      activities.find((activity, index) => {
        if (activity.uuid_activity_model === uuid) {
          const des = JSON.parse(activity.des)
          activities[index] = {
            ...activity,
            des: des?.checked.toString()
          }
        }
      })
    })
  }
  return activities
}

// Troca o conteúdo do campo "des" pelo uuid do documento (document - objectclass)
export function handleDocumentPhoto(
  activities: Activity[],
  activityModels: FlatInteractionModelData['activity_models']
) {
  const uuids = getUuidsForContentType(activityModels, 'document_photo')

  if (uuids.length > 0) {
    uuids.forEach((uuid) => {
      activities.find((activity, index) => {
        if (activity.uuid_activity_model === uuid) {
          const des = JSON.parse(activity.des)
          activities[index] = {
            ...activity,
            des: des.uuid
          }
        }
      })
    })
  }
  return activities
}

export function handleDate(
  activities: Activity[],
  activityModels: FlatInteractionModelData['activity_models'],
  language: string
) {
  const dateUuids = getUuidsForContentType(activityModels, 'date')
  const dateTimeUuids = getUuidsForContentType(activityModels, 'datetime')
  const uuids = [...dateUuids, ...dateTimeUuids]

  if (!uuids.length) {
    return activities
  }

  return activities.map((activity) => {
    if (uuids.includes(activity.uuid_activity_model)) {
      const locale = language as AllowedLocales
      const formatted = datepickerToISOFormat(activity.des, { locale })
      return {
        ...activity,
        des: formatted
      }
    }
    return activity
  }) as Activity[]
}

const getSelectedActivityValues = (
  activityValues: FlatInteractionModelData['activity_values'],
  selectedIds: string[]
) => {
  return pick(activityValues, selectedIds)
}

type FormatInteractionPostParams = {
  shownSteps: Step[]
  interactionModel: InteractionModel
  language: string
  transformers: ActivityTransformersMap
  draftUuid?: string
  draftVersion?: string
}

export async function getInteractionSavePayload({
  shownSteps,
  interactionModel,
  language,
  transformers,
  draftUuid,
  draftVersion
}: FormatInteractionPostParams) {
  const interactionModelData = interactionModel.flatData

  if (!interactionModelData) {
    return
  }

  const shownActivities = getSelectedActivityValues(
    interactionModelData.activity_values,
    shownSteps.flatMap((step) => step.activity_models)
  )

  const transformedActivities = await getTransformedActivities(
    shownActivities,
    interactionModelData.activity_models,
    transformers
  )

  const nonEmptyActivities = flatValuesToOldActivities(
    transformedActivities
  ).filter(({ des }) => des)

  let activities = nonEmptyActivities

  /*
    Tratamento especial para a activity model "document_photo";
    Deve manipular as activities trocando o conteúdo do campo "des"
    pelo uuid do documento (document - objectclass)
  */
  activities = handleDocumentPhoto(
    activities,
    interactionModelData.activity_models
  )

  activities = handleAgreementTerm(
    activities,
    interactionModelData.activity_models
  )

  activities = handleDate(
    activities,
    interactionModelData.activity_models,
    language
  )

  // coords em string
  const latitude = interactionModel.geolocation?.latitude?.toString() || ''
  const longitude = interactionModel.geolocation?.longitude?.toString() || ''

  const workflowUuid = interactionModel.workflowUuid && {
    uuid_workflow: interactionModel.workflowUuid
  }

  const body = [
    {
      ...workflowUuid,
      ...(draftUuid && { uuid: draftUuid }),
      ...(draftVersion && { version: draftVersion }),
      uuid_interaction_model: interactionModelData.uuid,
      uuid_user_creation_row: interactionModel.userUuid,
      uuid_user_modification_row: interactionModel.userUuid,
      uuid_language: interactionModel.language,
      latitude: latitude,
      longitude: longitude,
      date_start_interaction: interactionModel.dateStartInteraction,
      date_end_interaction: moment().format(),
      activities
    }
  ]

  return body
}

export async function validateInteractionModelCallback(
  interactionModel: FlatInteractionModelData,
  endpoint: string
) {
  const formatedInteractionModel =
    formatInteractionModelCallback(interactionModel)

  const res = await api.postInputValidation(
    formatedInteractionModel,
    endpoint,
    true
  )
  return res
}

type ValidateStepCallbackProps = ActivityValuesToGroupValidationFormatProps & {
  endpoint: string
}

export async function validateStepCallback({
  activityValues,
  stepUuid,
  endpoint
}: ValidateStepCallbackProps) {
  const formattedStep: GroupValidationCallbackRequestData =
    activityValuesToGroupValidationFormat({ activityValues, stepUuid })

  const res = await api.postInputValidation(formattedStep, endpoint, true)
  return res
}

type SaveInteractionParams = {
  shownSteps: Step[]
  interactionModel: InteractionModel
  language: string
  draftUuid?: string
  draftVersion?: string
}

export function useSaveInteraction() {
  const draw = useDrawActivityTransformer()

  const saveInteraction = async ({
    shownSteps,
    interactionModel,
    language,
    draftUuid,
    draftVersion
  }: SaveInteractionParams) => {
    const body = await getInteractionSavePayload({
      shownSteps,
      interactionModel,
      language,
      transformers: {
        draw
      },
      draftUuid,
      draftVersion
    })

    if (!draftUuid) {
      return await api.postSaveInteraction(body, true)
    }

    return await api.patchSaveInteraction(body, true)
  }

  return { saveInteraction }
}
